import {Injectable} from '@angular/core';
import {
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpInterceptor,
	HttpErrorResponse
} from '@angular/common/http';
import {catchError, first, map, Observable, switchMap, throwError} from 'rxjs';
import {AuthFacade} from '../store/auth-state/+state/auth.facade';
import {User} from '../models/User';
import {environment} from 'src/environments/environment';
import {Router} from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	readonly attemptHeaderName = 'X-Attempt';
	private readonly PUBLIC_URL_PATTERNS = ['/assets/', '/assets/i18n/', '.json'];

	constructor(
		private authFacade: AuthFacade,
		private router: Router
	) {}
	intercept(
		req: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		return this.authFacade.userData$.pipe(
			first(),

			map((user: User | null) => {
				return this.modifyRequestHeader(user?.token, req);
			}),
			switchMap((modifiedRequest: HttpRequest<any>) =>
				this.handleModifiedRequest(modifiedRequest, next)
			)
		);
	}

	private isPublicRequest(req: HttpRequest<any>): boolean {
		return this.PUBLIC_URL_PATTERNS.some(pattern => req.url.includes(pattern));
	}

	private modifyRequestHeader(
		token: string | null | undefined,
		req: HttpRequest<any>
	): HttpRequest<any> {
		if (this.isPublicRequest(req)) {
			return req;
		}

		if (!token) {
			return req;
		}
		if (req.url.includes(environment.webAppUrl)) {
			return req;
		}
		const attempt = (
			+(req.headers.get(this.attemptHeaderName) ?? 0) + 1
		).toString();
		return req.clone({
			setHeaders: {
				Authorization: `Bearer ${token}`,
				[this.attemptHeaderName]: attempt
			}
		});
	}
	private handleModifiedRequest(
		modifiedReq: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		return next.handle(modifiedReq).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status === 401) {
					const body = error.error;
					const isNotAuthorized =
						body?.name === 'NotAuthorizedError' && body?.code === 6;

					if (isNotAuthorized) {
						console.warn('User is not authorized, redirecting to login.');
						this.authFacade.logout();
						this.router.navigate(['/login']);
					}
				}

				return throwError(() => error);
			})
		);
	}
}
